import { supabase } from "./supabase";

// O IO do Diagnóstico RT, a página PÚBLICA (000373/000374, 30/10/2026) — zero regra.
//
// Quem abre a página não tem conta no hub. As quatro portas são funções que `anon` alcança, e
// nenhuma tabela é legível (`revoke all`): o questionário vem sem os pontos, o cadastro e o envio
// são conferidos no banco, a NOTA é do banco (`drt_pontuar`), e o relatório se abre pelo TOKEN do
// diagnóstico, nunca pelo e-mail, como o app antigo da Lovable fazia. Esta tela não valida
// campo, não soma ponto e não decide nível: ela mostra o que o banco responde, com a frase dele.
//
// O contato vai ao Data Gateway pela Edge Function `encaminhar-diagnostico`, que lê o cadastro do
// banco pelo token. O navegador não manda o nome nem o e-mail para ela.

export type Eixo = { nome: string; emoji: string; pontua: boolean };
export type Opcao = { valor: string; rotulo: string };
export type Pergunta = { numero: number; eixo: string; texto: string; opcoes: Opcao[] };
export type Questionario = { eixos: Eixo[]; perguntas: Pergunta[] };

export type Nivel = "alto" | "medio" | "baixo";

export type EixoDoRelatorio = {
  eixo: string;
  emoji: string;
  percentual: number;
  risco: Nivel;
  titulo: string | null;
  implicacoes: string[] | null;
};

export type RespostaDoRelatorio = {
  pergunta: number;
  eixo: string;
  texto: string;
  resposta: string;
  nivel: Nivel | null;
  alerta: string | null;
  faixa: "30" | "60" | "90" | null;
  acoes: string[] | null;
};

export type Relatorio = {
  criado_em: string;
  /** "atual" é o questionário de 25 perguntas; as versões anteriores trazem só o resumo (000374). */
  versao: "atual" | "27-perguntas" | "19-perguntas";
  percentual: number;
  nivel: { nivel: Nivel; rotulo: string; emoji: string; descricao: string };
  eixos: EixoDoRelatorio[];
  respostas: RespostaDoRelatorio[];
};

export type Recusa = { ok: false; motivo: string; mensagem: string };
export type Resultado = { ok: true; token: string } | Recusa;

const FALHA: Recusa = {
  ok: false,
  motivo: "falha",
  mensagem: "Não foi possível falar com o servidor agora. Confira a conexão e tente de novo.",
};

/** O questionário, ou `null` se a leitura falhou. */
export async function lerQuestionario(): Promise<Questionario | null> {
  const { data, error } = await supabase.rpc("drt_questionario");
  if (error || !data) return null;
  return data as Questionario;
}

/** As UTMs da página, como o formulário da Lovable as mandava. */
export function utmsDaBusca(busca: URLSearchParams): Record<string, string> {
  const utm: Record<string, string> = {};
  for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
    const v = busca.get(k);
    if (v) utm[k] = v;
  }
  return utm;
}

export async function cadastrar(nome: string, email: string, telefone: string, utm: Record<string, string>): Promise<Resultado> {
  const { data, error } = await supabase.rpc("drt_cadastrar", {
    p_nome: nome,
    p_email: email,
    p_telefone: telefone,
    p_utm: utm,
  });
  if (error || !data) return FALHA;
  return data as Resultado;
}

export async function enviar(cadastro: string, respostas: Record<string, string>): Promise<Resultado> {
  const { data, error } = await supabase.rpc("drt_enviar", { p_cadastro: cadastro, p_respostas: respostas });
  if (error || !data) return FALHA;
  return data as Resultado;
}

/** O relatório; `null` quando o token não é de diagnóstico nenhum; "falha" quando a leitura falhou. */
export async function lerRelatorio(token: string): Promise<Relatorio | null | "falha"> {
  if (!/^[0-9a-f]{36}$/.test(token)) return null;
  const { data, error } = await supabase.rpc("drt_relatorio", { p_token: token });
  if (error) return "falha";
  return (data as Relatorio | null) ?? null;
}

/**
 * Pede à Edge Function que leve o contato ao Data Gateway, e NÃO espera: o diagnóstico não pode
 * ficar parado por causa do repasse, e a função registra o resultado no próprio cadastro. O
 * `keepalive` deixa o pedido terminar mesmo se a página trocar de tela no meio.
 *
 * O pedido é "simples" de propósito (texto puro, sem cabeçalho próprio): assim o navegador não faz
 * a pré-verificação de CORS, que é o passo que um pedido com `keepalive` pode perder quando a
 * página muda de tela. A resposta não é lida, e a função lê o corpo como JSON igual. Sem chave:
 * com `verify_jwt = false` o gateway não pede, como na `descadastrar-pesquisas`.
 */
export function encaminharContato(cadastro: string): void {
  const base = import.meta.env.VITE_SUPABASE_URL as string;
  void fetch(`${base}/functions/v1/encaminhar-diagnostico`, {
    method: "POST",
    keepalive: true,
    headers: { "content-type": "text/plain;charset=UTF-8" },
    body: JSON.stringify({ cadastro, url: window.location.href }),
  }).catch(() => {});
}

// ---------------------------------------------------------------------------
// O que fica no navegador de quem responde
// ---------------------------------------------------------------------------
// O CADASTRO fica no `localStorage`: quem volta para fazer outro diagnóstico não se cadastra de
// novo (o que mandaria o contato duas vezes ao Data Gateway). O ANDAMENTO das respostas fica no
// `sessionStorage`, para recarregar a página não apagar o que já foi respondido. E o último
// relatório fica guardado, porque a única chave dele é o link: sem isso, quem fecha a aba perde o
// relatório. Nada disso é dado do cadastro além do primeiro nome, que a página usa para dizer
// "você já se cadastrou".

const CHAVE_CADASTRO = "drt:cadastro";
const CHAVE_ANDAMENTO = "drt:andamento";
const CHAVE_ULTIMO = "drt:ultimo";

export type CadastroGuardado = { token: string; nome: string };

function ler<T>(armazem: Storage, chave: string): T | null {
  try {
    const bruto = armazem.getItem(chave);
    return bruto ? (JSON.parse(bruto) as T) : null;
  } catch {
    return null;
  }
}

function gravar(armazem: Storage, chave: string, valor: unknown): void {
  try {
    if (valor === null) armazem.removeItem(chave);
    else armazem.setItem(chave, JSON.stringify(valor));
  } catch {
    // Navegador sem armazenamento (aba anônima de alguns navegadores): a página funciona igual,
    // só não lembra nada depois de recarregar.
  }
}

export const lembrar = {
  cadastro: () => ler<CadastroGuardado>(localStorage, CHAVE_CADASTRO),
  guardarCadastro: (c: CadastroGuardado | null) => gravar(localStorage, CHAVE_CADASTRO, c),
  andamento: () => ler<Record<string, string>>(sessionStorage, CHAVE_ANDAMENTO),
  guardarAndamento: (r: Record<string, string> | null) => gravar(sessionStorage, CHAVE_ANDAMENTO, r),
  ultimo: () => ler<string>(localStorage, CHAVE_ULTIMO),
  guardarUltimo: (token: string | null) => gravar(localStorage, CHAVE_ULTIMO, token),
};
